import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClientContactDTO, ClientDTO, ClientOwnerDTO, getClientById } from 'wisestep-api';
import { WsTitleBanner, FormWizard } from 'wisestep-base';

import { WsBreadCrumbs } from '../../components/WsBreadCrumbs';
import { isBlank } from '../../utils/StringUtils';
import { getClientsBreadCrumbList } from '../clientDetails/helper/clientDetailsHelper';

import ClientBasicDetailsForm from './components/ClientBasicDetailsForm';
import ClientContacts from './components/ClientContacts';
import ClientDetailsFormNew from './components/ClientDetailsFormNew';
import { createClientPayload } from './helper/ClientFormHelper';

type ClientFormParams = {
  clientId: string;
  parentId: string;
};

const ClientForm = () => {
  const navigate = useNavigate();
  const params = useParams<ClientFormParams>();
  const [formWizardCurrentStep, setFormWizardCurrentStep] = useState(0);
  const [formWizardCurrentChildStep, setFormWizardCurrentChildStep] = useState(0);
  const [client, setClient] = useState<ClientDTO>({ ...createClientPayload });
  const [clientId, setClientId] = useState<string>(params.clientId ?? '');
  const [parentId, setParentId] = useState<string>();
  const [clientContacts, setClientContacts] = useState<ClientContactDTO[]>([]);
  const [clientOwner, setClientOwner] = useState<ClientOwnerDTO | null>(null);
  const parentIdParam: string = params.parentId ?? '';
  const [clientFoundInDirectHire, setClientFoundInDirectHire] = useState<boolean>(false);

  const fetchClient = useCallback(async () => {
    if (clientId) {
      const response = await getClientById(clientId);
      if (response.data.data) {
        setClient(response.data.data);
      }
    }
  }, [clientId]);

  useEffect(() => {
    fetchClient();
  }, [clientId, fetchClient]);

  useEffect(() => {
    if (client && clientContacts.length > 0) {
      const updatedContacts = clientContacts.map((contact) => ({
        ...contact,
        clientId: client.id,
      }));
      setClientContacts(updatedContacts);
    }
  }, [client]);

  useEffect(() => {
    if (formWizardCurrentStep === 0) fetchClient();
  }, [fetchClient, formWizardCurrentStep]);

  useEffect(() => {
    if (!isEmpty(parentIdParam)) {
      setParentId(parentIdParam);
    }
  }, [parentIdParam]);

  const onFormWizardArrowClick = () => navigate('/clients');

  const formWizardMenuItemClick = (id: number) => {
    setFormWizardCurrentStep(id);
  };

  const menuItems = [
    {
      id: 0,
      label: 'Client Information',
      onClick: () => formWizardMenuItemClick(0),
      subList: [
        {
          id: 0,
          label: 'Basic Details',
          onClick: () => setFormWizardCurrentChildStep(0),
        },
        {
          id: 1,
          label: 'Other Details',
          onClick: () => setFormWizardCurrentChildStep(1),
        },
      ],
    },
    {
      id: 1,
      label: 'Client Contacts',
      onClick: () => formWizardMenuItemClick(1),
    },
  ];

  if (!isBlank(clientId) && !client) return null;
  return (
    <>
      <WsBreadCrumbs
        breadCrumbsList={getClientsBreadCrumbList('ADD_CLIENT', 'Add Client Details')}
      />
      <FormWizard
        menuItems={menuItems}
        onArrowButtonClick={onFormWizardArrowClick}
        currentStep={formWizardCurrentStep}
        title="Client List"
      >
        <WsTitleBanner title="Client Details" backButton={false} />
        {formWizardCurrentStep == 0 && formWizardCurrentChildStep == 0 && (
          <ClientBasicDetailsForm
            client={client}
            setClient={setClient}
            setClientFoundInDirectHire={setClientFoundInDirectHire}
            setFetchedContacts={setClientContacts}
            setFormWizardCurrentChildStep={setFormWizardCurrentChildStep}
          />
        )}
        {formWizardCurrentStep == 0 && formWizardCurrentChildStep == 1 && (
          <ClientDetailsFormNew
            source="ClientForm"
            client={client}
            setClient={(client) => {
              setClient(client);
              setClientId(client.id);
            }}
            setClientContacts={setClientContacts}
            setFormWizardCurrentStep={setFormWizardCurrentStep}
            setClientOwner={setClientOwner}
            clientContactsReceived={clientContacts}
            clientFoundInDH={clientFoundInDirectHire}
          />
        )}
        {formWizardCurrentStep == 1 && (
          <ClientContacts
            clientId={clientId || client?.id}
            setFormWizardCurrentStep={setFormWizardCurrentStep}
            parentId={parentId}
            clientContactsReceived={clientContacts}
            clientOwner={clientOwner}
          />
        )}
      </FormWizard>
    </>
  );
};

export default ClientForm;
